import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";
import { SpacingVariants } from "../../../types/spacing";
import classes from "./list.module.css";

type ListCommonProps = HTMLAttributes<HTMLUListElement> & {
  gap?: SpacingVariants;
  direction?: "horizontal" | "vertical";
};

type ListSnapProps = ListCommonProps & {
  snap: true;
  snapOffset?: string;
};

type ListNonSnapProps = ListCommonProps & {
  snap?: false;
  snapOffset?: never;
};

export type ListProps = ListSnapProps | ListNonSnapProps;

export const Root = forwardRef<HTMLUListElement, ListProps>(
  (
    {
      snap,
      gap,
      snapOffset,
      style,
      className,
      direction = "horizontal",
      ...props
    },
    ref,
  ) => {
    const inlineStyle = {
      ...style,
      ...(gap ? { "--list-gap": `var(--spacing-${gap})` } : null),
      ...(snap && snapOffset ? { "--list-snap-offset": snapOffset } : null),
    };

    return (
      <ul
        ref={ref}
        style={inlineStyle}
        className={clsx(
          classes.root,
          snap && classes.snap,
          gap && classes.gap,
          className,
          direction === "horizontal" ? classes.horizontal : classes.vertical,
          snap &&
            (direction === "horizontal"
              ? classes.snapHorizontal
              : classes.snapVertical),
        )}
        {...props}
      />
    );
  },
);

Root.displayName = "ListRoot";

export const Item = forwardRef<HTMLLIElement, HTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => {
    return (
      <li ref={ref} className={clsx(classes.item, className)} {...props} />
    );
  },
);

Item.displayName = "ListItem";
